import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Avatar,
  Stack,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  TrendingUp,
  SportsScore,
  EmojiEvents,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { User } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { getAvatarUrl, isDefaultAvatar } from '../../utils/avatar';

export const AdminUserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser?.is_admin || !userId) return;
    const id = parseInt(userId, 10);
    if (Number.isNaN(id)) {
      setError('Invalid user ID');
      setLoading(false);
      return;
    }
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.getUser(id);
        setUser(res.data);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          'Failed to load user';
        setError(String(msg));
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [currentUser?.is_admin, userId]);

  if (!currentUser?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">{t('adminUserProgress.accessDenied')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message={t('adminUserProgress.loading')} />;
  }

  if (error || !user) {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/users')}>
          {t('adminUserProgress.backToUsers')}
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error ?? t('adminUserProgress.userNotFound')}
        </Alert>
      </Box>
    );
  }

  const currentElo = user.elo_rating ?? user.initial_elo ?? 0;

  return (
    <Box sx={{ p: 2 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/users')}
        sx={{ mb: 2 }}
      >
        {t('adminUserProgress.backToUsers')}
      </Button>

      <Box display="flex" alignItems="center" gap={3} sx={{ mb: 3 }}>
        <Avatar
          src={getAvatarUrl(user.avatar_url) || undefined}
          sx={{ width: 80, height: 80 }}
        >
          {isDefaultAvatar(user.avatar_url) ? user.name?.charAt(0)?.toUpperCase() || '?' : null}
        </Avatar>
        <Box flex={1}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {user.name}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {user.email}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate('/admin/users', { state: { editUserId: user.id } })}
            >
              {t('adminUsers.edit')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<TrendingUp />}
              onClick={() => navigate(`/admin/users/${user.id}/progress`)}
            >
              {t('adminUserProgress.viewProgress')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Card sx={{ maxWidth: 400 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('adminUserProgress.currentElo')}
          </Typography>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {currentElo.toFixed(1)}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              icon={<SportsScore />}
              label={`${user.total_matches ?? 0} ${t('adminUsers.matches')}`}
              size="small"
            />
            <Chip
              icon={<EmojiEvents />}
              label={`${(user.win_rate ?? 0).toFixed(1)}% ${t('adminUsers.winRate')}`}
              size="small"
            />
            <Chip
              label={user.is_active ? t('adminUsers.active') : t('adminUsers.inactive')}
              size="small"
              color={user.is_active ? 'success' : 'default'}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
